import * as React from 'react';
import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FvcAnalysisSet, SessionNote, SubjectNote, TestNote } from 'webportalclient';
import { useSecurityContext } from '../../Core/SecurityContext';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { NotesModal } from "../../misc/components/NotesModal";
import { LeanSessionMetadata } from './SessionMetadataTable';
import { OverreadChallengeStatusEnum } from '../../misc/api/apiTypes';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';

export interface ContestOverreaderDecisionPanelProps {
    notesLinkName: string;
    notes: SessionNote[] | SubjectNote[] | TestNote[];
    isLoading: boolean;
    showNotesCount?: boolean;
    onAddNote: (annotation: string) => void;
}

interface ExpectedMatchParams {
    analysisSetId: string;
}

export interface ModalVisibilityParams {
    isModalVisible: boolean;
    setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}



export const ChallengeOverreaderDecisionPanel = () => {
    const [isModalVisibility, setIsModalVisibility] = React.useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);
    const { analysisSetId } = useParams();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>();
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<FvcAnalysisSet>();
    const client = useWebPortalApiClient();
    const [notes, setNotes] = useState(null);
    const { currentUser } = useSecurityContext();
    const { countries } = useReferenceContextProvider();

    const navigate = useNavigate();

    const addOverreaderDecisionChallenge = async (sessionId: string, annotation: string) => {
        await client.client.postSessionNote({
            sessionId: sessionId,
            userEmailAddress: currentUser.Email,
            comment: "[Challenge Over-reader Decision] " + annotation,
            recorded: new Date().toISOString()
        });

        await client.client.patchFvcSession({
            overreadChallengeStatus: OverreadChallengeStatusEnum.Challenged
        }, sessionId, annotation);

        await fetchSession(sessionId);
    }

    const fetchSessionNotes = async (sessionId: string) => {
        const fvcSession = await fetchSession(sessionId);
        setNotes(fvcSession.notes.filter(n => n.comment.startsWith("[Challenge Over-reader Decision]")));
    }

    const fetchSession = async (sessionId: string) => {
        const fvcSessionResult = await client.client.getFvcSession(sessionId, undefined, "notes,tests,visit($select=name;$expand=subject($select=id,number;$expand=site($select=id,number,name,country)))");

        setSessionMetadata({
            Id: fvcSessionResult.id,
            SiteId: fvcSessionResult.visit.subject.site.id,
            SiteName: fvcSessionResult.visit.subject.site.name,
            SiteCountryCode: fvcSessionResult.visit.subject.site.country,
            SiteCountryName: countries.find(c => c.id === fvcSessionResult.visit.subject.site.country)?.name,
            SiteNumber: fvcSessionResult.visit.subject.site.number,
            Technician: fvcSessionResult.technician,
            SubjectId: fvcSessionResult.visit.subject.id,
            SubjectNumber: fvcSessionResult.visit.subject.number,
            VisitName: fvcSessionResult.visit.name,
            SessionName: fvcSessionResult.name,
            SessionDateTime: fvcSessionResult.occurred,
            SessionTypeId: fvcSessionResult.sessionType,
            ReceivedDateTime: fvcSessionResult.created,
            SessionTypeName: fvcSessionResult.sessionType,
            Status: "",
            StatusId: "",
            Overreader: "",
            AnalysisSets: [],
        });

        return fvcSessionResult;
    }

    const getAnalysisSet = async () => {
        const analysisSetResult = await client.client.getSpirometryAnalysisSet(analysisSetId);
        return analysisSetResult;
    }

    React.useEffect(() => {
        async function fetchData() {
            const analysisSetResult = await getAnalysisSet();
            await setOverreadAnalysisSet(analysisSetResult);
            await fetchSessionNotes(analysisSetResult.sessionId);
            setIsModalVisibility(true);
        }
        fetchData();
    }, []);

    return (
        <>
            <NotesModal
                isVisible={isModalVisibility}
                header={"Challenge Over-read Decision"}
                isAddingNote={false}
                changeButtonText={"Challenge Over-Reader Decision"}
                notes={notes}
                onAdd={annotation => {
                    setIsAddingNewNote(true);
                    addOverreaderDecisionChallenge(overreadAnalysisSet.sessionId, annotation);
                    setIsAddingNewNote(false);
                    navigate(-1);
                }}
                onCloseModal={() => navigate(-1)}
                disabled={false}
            />
        </>
    );
}
